import Script from 'next/script'
import React, { useState, useEffect } from 'react'
import { FormikProvider, useFormik } from 'formik'
import axios from 'utility/axios'
import * as Yup from 'yup'
import { Row, Col } from 'react-bootstrap'
import { useRouter } from 'next/router'
import * as google from 'utility/google'
import { isPartnerEnterprise } from 'utility/partners'
import { BlockPicker } from 'react-color'
import { toast } from 'react-toastify'

import FormInput from 'components/common/forms/elements/FormInput'
import FormLabel from 'components/common/forms/elements/FormLabel'
import Button from 'components/common/Button'
import Text from 'components/common/Text'
import Card from 'components/common/cards/Card'
import RadioInput from 'components/common/forms/elements/RadioInput'
import Spinner from 'components/common/Spinner'
import Link from 'components/common/Link'
import NonprofitDropdown from 'components/common/dropdowns/NonprofitDropdown'
import { usePartner } from 'components/providers/PartnerProvider'
import { useApp } from 'components/providers/AppProvider'

import { formatPhoneNumber } from 'utility/numbers'
import { COLORS } from 'utility/colors'
import { POST_PARTNER, POST_PARTNER_UPDATE } from 'utility/endpoints'
import * as logrocket from 'utility/logrocket'
import { toBase64 } from 'utility/files'
import * as crisp from 'utility/crisp'
import { useUser } from 'hooks/api/user/useUser'
import { useSelf } from 'components/providers/SelfProvider'
import CardTitle from '../cards/CardTitle'
import { toSlug } from 'utility/string'
import { ImageUploaderWithCrop } from './ImageUploaderWithCrop'
import { useTenantProfile } from 'hooks/api/tenant/useTenantProfile'
import { origin } from 'utility/domain'
import { PartnerMarketsDropdown } from 'components/common/dropdowns/PartnerMarketsDropdown'
import { PartnerIndustryTypesDropdown } from 'components/common/dropdowns/PartnerIndustryTypesDropdown'
import { PartnerCategoriesDropdown } from 'components/common/dropdowns/PartnerCategoriesDropdown'

export const PartnerForm = ({
	signup = false,
	onSignupSuccess = () => {},
	backgroundColor = COLORS.MUTED_GRAY,
}) => {
	const router = useRouter()
	const { data: tenantProfile } = useTenantProfile()
	const tenantName = tenantProfile?.name
	const primaryColor = tenantProfile?.theme?.primaryColor
	const secondaryColor = tenantProfile?.theme?.secondaryColor
	const hasPartnerCommissions = tenantProfile?.features?.partnerCommissions

	const { userSlug, slug: partnerSlugId, type } = router?.query

	const { refetch: refetchUser } = useUser(userSlug)
	const { fetchPartner, partner: partnerObj } = usePartner()

	const { fetchSelfFromAuthToken, isAdmin } = useSelf()
	const { setAuthToken } = useApp()

	const partner = signup ? null : partnerObj?.data

	const [nonProfit, setNonprofit] = useState(partner?.referralCommissionTo)
	const [marketId, setMarketId] = useState(partner?.internalMarketId)
	const [industryTypeId, setIndustryTypeId] = useState(partner?.industryTypeId)
	const [categoryId, setCategoryId] = useState(partner?.categoryID)
	const [categoryName, setCategoryName] = useState(partner?.categoryName)

	const isEnterprisePartner =
		isAdmin && categoryName ? categoryName === 'Enterprise' : isPartnerEnterprise(partnerObj.data)

	const [error, setError] = useState()
	const [avatar, setAvatar] = useState()
	const [whiteLogo, setWhiteLogo] = useState()
	const [colorLogo, setColorLogo] = useState()
	const [success, setSuccess] = useState(false)

	const [loading, setLoading] = useState(false)

	const isUpdateForm = !!partner?.partnerId

	useEffect(() => {
		if (marketId && industryTypeId) return

		if (!marketId) setMarketId(partner?.marketId)

		if (!industryTypeId) setIndustryTypeId(partner?.industryTypeId)
	}, [partner, marketId, industryTypeId])

	console.log({ partner })
	const agentForm = useFormik({
		initialValues: {
			firstName: partner?.firstName || '',
			lastName: partner?.lastName || '',
			slug: toSlug(partner?.slug) || '',
			teamName: partner?.teamName || '',
			companyName: partner?.companyName || '',
			licensed: partner?.licenseNumber ? 'Yes' : 'No',
			licenseNumber: partner?.licenseNumber || '',
			phoneNumber: partner?.phoneNumber || '',
			emailAddress: partner?.emailAddress || '',
			personalWebsiteUrl: '',
			enterprisePrimaryColor: partner?.enterpriseAttributes?.primaryColor || primaryColor,
			enterpriseSecondaryColor: partner?.enterpriseAttributes?.secondaryColor || secondaryColor,

			...(isAdmin
				? {
						commissionEligible: partner?.commissionEligible ?? true,
						firstYearDiscountPct: partner?.firstYearDiscountPct || 0,
						renewalDiscountPct: partner?.renewalDiscountPct || 0,
						isActive: partner?.isActive ?? true,
						showProfile: partner?.showProfile ?? true,
					}
				: {}),
		},
		onSubmit: async (values) => {
			if (loading) return

			setError(null)
			setLoading(true)

			const base64Avatar = avatar && (await toBase64(avatar))
			const avatarType = avatar?.type

			const base64ColorLogo = colorLogo && (await toBase64(colorLogo))
			const colorLogoType = colorLogo?.type

			const base64WhiteLogo = whiteLogo && (await toBase64(whiteLogo))
			const whiteLogoType = whiteLogo?.type

			try {
				let number = values.phoneNumber && values.phoneNumber.replace(/[^0-9]/g, '')

				const slug = values.slug || `${values.firstName}-${values.lastName}`

				let data = {
					...values,
					slugName: toSlug(slug),
					phoneNumber: number,
					avatarImageData: base64Avatar ? base64Avatar.split(',')[1] : '',
					avatarImageFileType: avatarType ? avatarType.split('/')[1] : '',
					isAvatarImageChanged: !!base64Avatar,

					enterpriseColorLogoImageData: base64ColorLogo ? base64ColorLogo.split(',')[1] : '',
					enterpriseColorLogoImageFileType: colorLogoType ? colorLogoType.split('/')[1] : '',
					isEnterpriseColorLogoImageChanged: !!base64ColorLogo,

					enterpriseWhiteLogoImageData: base64WhiteLogo ? base64WhiteLogo.split(',')[1] : '',
					enterpriseWhiteLogoImageFileType: whiteLogoType ? whiteLogoType.split('/')[1] : '',
					isEnterpriseWhiteLogoImageChanged: !!base64WhiteLogo,

					enterprisePartnerSlugName: signup ? partnerSlugId : '',
					internalMarketId: marketId || null,
					industryTypeId: industryTypeId || null,
					categoryID: categoryId || null,

					showProfile: isAdmin ? values.showProfile : true,
					userSlugId: userSlug ? userSlug : null,
					referralCommissionTo: nonProfit,
					licenseNo: values.licenseNumber || '',
					companyWebsiteUrl: '',
					partnerId: partner?.partnerId || null,
				}

				let response
				if (isUpdateForm) {
					response = await axios.post(POST_PARTNER_UPDATE, data)

					if (!response?.data) return

					const partnerSlug = router?.query?.slug
					fetchPartner({ slug: partnerSlug })
					refetchUser()
					fetchSelfFromAuthToken()
					toast.success('Your partner profile has been updated!')
				} else {
					response = await axios.post(POST_PARTNER, data)

					if (!response?.data) return

					logrocket.track('Completed Agent Registration')
					crisp.customEvent('Completed Agent Registration')
					logrocket.identify(values.emailAddress, {
						name: `${values.firstName} ${values.lastName}`,
						email: values.emailAddress,
						phoneNumber: number,
						homeowner: false,
						agent: true,
					})

					crisp.identify('email', values.emailAddress)
					crisp.identify('phone', number)
					crisp.identify('nickname', `${values.firstName} ${values.lastName}`)
					crisp.customData('isAgent', true)

					google.event({
						action: 'form',
						category: 'conversion',
						label: 'partner-success', // Todo - update this with some key identifier
						value: `${values.emailAddress} Signed Up as a Partner`,
					})

					google.event({
						action: 'partner_signup',
						category: 'conversion',
					})

					const newAuthToken = response?.data?.authToken
					if (!newAuthToken) {
						console.log(
							'AddPartner succeeded but response has no authToken. Response data keys: ',
							response?.data && typeof response.data === 'object'
								? Object.keys(response.data)
								: typeof response?.data,
						)
						setLoading(false)
						return setError(
							'Your partner profile was created, but we could not sign you in automatically. Please sign in.',
						)
					}

					setAuthToken(newAuthToken)
					// Only refetch when we actually have a user slug — on signup pages
					// without one (e.g. enterprise), refetch() ignores `enabled` and
					// would fire a request for /user/.../undefined
					if (userSlug) refetchUser()
					setSuccess(true)

					// Establish the session (populate selfUser so isSignedIn is true)
					// BEFORE redirecting — otherwise the destination page mounts
					// signed-out and can bounce to /auth/signin as unauthorized
					try {
						await fetchSelfFromAuthToken(newAuthToken)
					} catch (selfErr) {
						// Signup itself succeeded and the token is saved; SelfProvider's
						// authToken effect will retry the self fetch, so still proceed
						console.log('Error fetching self after signup: ', selfErr)
					}

					onSignupSuccess(response?.data)
				}

				setLoading(false)
			} catch (err) {
				setLoading(false)
				if (err.response) {
					const message = err?.response?.data

					const messages = Array.isArray(message)
						? message.map((m) => m?.title).filter(Boolean)
						: message
							? [message]
							: []

					if (messages.length) {
						const finalErrorCopy = messages.map((text, i) =>
							text === 'Email Address is already used.' ? (
								<React.Fragment key={i}>
									This email is already associated with an account. Please{' '}
									<Link href={`/auth/signin`} target='_blank'>
										CLICK HERE to login to your account.
									</Link>
								</React.Fragment>
							) : (
								<React.Fragment key={i}>{text}</React.Fragment>
							),
						)
						return setError(finalErrorCopy)
					}

					return setError(
						`Sorry, there's an unknown system error. Please ensure you've filled out all required fields and try again. Sometimes trying a different browser or credit card helps. If it still doesn't work, please reach out to our support team via the webchat feature on our website.`,
					)
				}

				console.log('Error: ', err)
				setError('Sorry we had trouble - please try again.')
			}
		},
		validationSchema: Yup.object({
			firstName: Yup.string()
				.min(2, 'Must be between 2 & 30 characters.')
				.max(30, 'Must be between 2 & 30 characters.')
				.required('Required'),
			lastName: Yup.string()
				.min(2, 'Must be between 2 & 30 characters.')
				.max(30, 'Must be between 2 & 30 characters.')
				.required('Required'),
			slug: Yup.string()
				.min(2, 'Must be between 2 & 30 characters.')
				.max(30, 'Must be between 2 & 30 characters.'),
			teamName: Yup.string(),
			companyName: Yup.string().required('Required'),
			phoneNumber: Yup.string().required('Required').min(10, 'Must be 10 characters'),
			emailAddress: Yup.string().email('Must be a valid email address').required('Required'),
			firstYearDiscountPct: Yup.number()
				.integer('Must be a whole number')
				.min(0, 'Must be between 0 and 100')
				.max(100, 'Must be between 0 and 100'),
			renewalDiscountPct: Yup.number()
				.integer('Must be a whole number')
				.min(0, 'Must be between 0 and 100')
				.max(100, 'Must be between 0 and 100'),
		}),
		validateOnChange: false,
		validateOnBlur: false,
	})

	const labelColorStyle = {
		color: COLORS.GRAY,
		fontWeight: 'bold',
		fontSize: '14px',
		marginBottom: 7,
	}

	const errorColorStyle = {
		color: COLORS.RED,
		marginBottom: 7,
	}

	const onSelectNonprofit = (nonProfitObject) => {
		setNonprofit(nonProfitObject?.title)
	}

	return (
		<>
			{success && !isUpdateForm ? (
				<Script async src='https://tag.simpli.fi/sifitag/4dbeebac-f320-4060-bef7-110298ccaa9e' />
			) : null}
			<FormikProvider value={agentForm}>
				<form onSubmit={agentForm.handleSubmit}>
					<Card noShadow style={{ padding: 20, backgroundColor }}>
						<div>
							<Row>
								<Col xs='12' md={'6'} lg='5'>
									<FormInput
										type='text'
										id='firstName'
										name='firstName'
										label={'FIRST NAME'}
										labelStyle={labelColorStyle}
										errorStyle={errorColorStyle}
										onChange={agentForm.handleChange}
										value={agentForm.values.firstName}
										placeholder={'Enter first name'}
										required
										error={agentForm.errors.firstName}
									/>
								</Col>
								<Col xs='12' md={'6'} lg='5'>
									<FormInput
										type='text'
										id='lastName'
										name='lastName'
										label={'LAST NAME'}
										labelStyle={labelColorStyle}
										errorStyle={errorColorStyle}
										onChange={agentForm.handleChange}
										value={agentForm.values.lastName}
										placeholder={'Enter last name'}
										required
										error={agentForm.errors.lastName}
									/>
								</Col>
							</Row>

							<Row>
								<Col xs='12' lg='10'>
									<FormInput
										type='text'
										id='slug'
										name='slug'
										hint={`${origin}/partners/`}
										label={'REFERRAL URL'}
										disabled={isUpdateForm}
										labelStyle={labelColorStyle}
										errorStyle={errorColorStyle}
										onChange={agentForm.handleChange}
										value={
											!agentForm.values.slug
												? `${toSlug(
														`${agentForm.values.firstName}${
															agentForm.values.lastName ? `- ${agentForm.values.lastName}` : ''
														}`,
													)}`
												: `${toSlug(agentForm.values.slug)}`
										}
										placeholder={'Slug'}
										required
										error={agentForm.errors.slug}
									/>
								</Col>
							</Row>

							<Row>
								<Col xs='12' lg='10'>
									<FormInput
										type='text'
										id='phoneNumber'
										name='phoneNumber'
										label={'PHONE'}
										labelStyle={labelColorStyle}
										errorStyle={errorColorStyle}
										icon={'phone'}
										iconAfter
										color={'white'}
										onChange={(e) => {
											const value = e.target.value
											const formattedPhoneNumber = formatPhoneNumber(value)
											const finalPhoneNumber = formattedPhoneNumber ? formattedPhoneNumber : value
											agentForm.setFieldValue('phoneNumber', finalPhoneNumber)
										}}
										placeholder={'(xxx) xxx-xxxx'}
										value={formatPhoneNumber(agentForm.values.phoneNumber)}
										required
										error={agentForm.errors.phoneNumber}
									/>
								</Col>
							</Row>

							<Row>
								<Col xs='12' lg='10'>
									<FormInput
										type='text'
										id='emailAddress'
										icon={'envelope'}
										color={'white'}
										label={'EMAIL'}
										labelStyle={labelColorStyle}
										errorStyle={errorColorStyle}
										iconAfter
										name='emailAddress'
										onChange={agentForm.handleChange}
										placeholder={'Enter email address'}
										value={agentForm.values.emailAddress}
										required
										error={agentForm.errors.emailAddress}
									/>
								</Col>
							</Row>

							<ImageUploaderWithCrop
								label='HEADSHOT (or LOGO)'
								defaultImagePreview={partner?.avatarImageUrl}
								onChangeFile={(file) => {
									setAvatar(file)
								}}
							/>

							<Row>
								<Col xs='12' lg='10'>
									<PartnerMarketsDropdown
										labelStyle={labelColorStyle}
										value={marketId}
										style={{ marginBottom: 15 }}
										onSelect={(selectedMarket) => setMarketId(selectedMarket?.data?.id)}
									/>
								</Col>
							</Row>

							<Row>
								<Col xs='12' lg='10'>
									<PartnerIndustryTypesDropdown
										labelStyle={labelColorStyle}
										value={industryTypeId}
										style={{ marginBottom: 15 }}
										onSelect={(selectedIndustryType) =>
											setIndustryTypeId(selectedIndustryType?.data?.id)
										}
									/>
								</Col>
							</Row>

							<Row>
								<Col xs='12' lg='10'>
									<FormInput
										type='text'
										id='companyName'
										name='companyName'
										required
										label={'COMPANY NAME'}
										labelStyle={labelColorStyle}
										errorStyle={errorColorStyle}
										placeholder={'Company Name'}
										onChange={agentForm.handleChange}
										value={agentForm.values.companyName}
										error={agentForm.errors.companyName}
									/>
								</Col>
							</Row>

							<Row>
								<Col xs='12' lg='10'>
									<FormInput
										type='text'
										id='teamName'
										label={'TEAM NAME'}
										labelStyle={labelColorStyle}
										errorStyle={errorColorStyle}
										name='teamName'
										placeholder={'Team Name (optional)'}
										onChange={agentForm.handleChange}
										value={agentForm.values.teamName}
										error={agentForm.errors.teamName}
									/>
								</Col>
							</Row>

							<Row>
								<Col xs='12'>
									<FormLabel
										required
										style={{
											...labelColorStyle,
											fontSize: 14,
											color: COLORS.GRAY,
										}}
									>
										Are you a licensed real estate professional?
									</FormLabel>
									<RadioInput
										name={'licensed'}
										formContainerStyle={{
											display: 'flex',
											flexDirection: 'row',
										}}
										labelStyle={labelColorStyle}
										errors={agentForm.errors.licensed}
										onChange={(e) => {
											agentForm.setFieldValue('licensed', e?.target?.value)
										}}
										value={agentForm.values.licensed}
										options={[
											{ label: 'Yes', value: 'Yes' },
											{ label: 'No', value: 'No' },
										]}
									/>
								</Col>
							</Row>

							<Row>
								<Col
									xs='12'
									md='10'
									className={`${agentForm.values.licensed === 'Yes' ? 'd-block' : 'd-none'}`}
								>
									<FormInput
										type='text'
										id='licenseNumber'
										name='licenseNumber'
										label={'LICENSE NUMBER'}
										labelStyle={labelColorStyle}
										errorStyle={errorColorStyle}
										placeholder={'License Number'}
										onChange={agentForm.handleChange}
										tooltip={`Professionals in a role requiring a real estate license (TREC, NMLS, TDLR, TDI, etc) should enter it here to maintain a free ${tenantName} partner account, qualify for referral commissions and get early get early access to new products and services.`}
										required={agentForm.values.licensed === 'Yes' ? true : false}
										value={agentForm.values.licenseNumber?.substring(0, 20)}
										error={agentForm.errors.licenseNumber}
									/>
								</Col>
							</Row>
						</div>

						{hasPartnerCommissions && signup ? (
							<NonprofitDropdown
								partner={partner}
								enterprisePartnerSlug={partnerSlugId}
								onSelect={onSelectNonprofit}
								value={nonProfit}
							/>
						) : null}

						{isEnterprisePartner && (
							<div style={{ marginTop: 20, marginBottom: 10 }}>
								<CardTitle>Enterprise Partner Fields</CardTitle>
								<Row style={{ marginTop: 20, marginBottom: 20 }}>
									<Col xs='12' md='6' lg='3'>
										<ImageUploaderWithCrop
											label='COLOR LOGO'
											maxWidth={300}
											icon='image'
											buttonLabel='Select Logo'
											defaultImagePreview={partner?.enterpriseAttributes?.colorLogoUrl}
											onChangeFile={(file) => {
												setColorLogo(file)
											}}
										/>
									</Col>
									<Col xs='12' md='6' lg='3'>
										<ImageUploaderWithCrop
											label='WHITE LOGO'
											maxWidth={300}
											icon='image'
											buttonLabel='Select Logo'
											backgroundColor={COLORS.GRAY}
											defaultImagePreview={partner?.enterpriseAttributes?.whiteLogoUrl}
											onChangeFile={(file) => {
												setWhiteLogo(file)
											}}
										/>
									</Col>
									<Col xs='12' md='6' lg='3'>
										<FormLabel style={labelColorStyle}>PRIMARY COLOR</FormLabel>
										<div style={{ marginBottom: 10 }}>
											<BlockPicker
												width='100%'
												id='enterprisePrimaryColor'
												name='enterprisePrimaryColor'
												triangle={'hide'}
												colors={[]}
												onChange={(color) =>
													agentForm.setFieldValue('enterprisePrimaryColor', color?.hex)
												}
												color={agentForm.values.enterprisePrimaryColor}
											/>
										</div>
									</Col>
									<Col xs='12' md='6' lg='3'>
										<FormLabel style={labelColorStyle}>SECONDARY COLOR</FormLabel>
										<div style={{ marginBottom: 10 }}>
											<BlockPicker
												width='100%'
												id='enterpriseSecondaryColor'
												name='enterpriseSecondaryColor'
												triangle={'hide'}
												colors={[]}
												onChange={(color) =>
													agentForm.setFieldValue('enterpriseSecondaryColor', color?.hex)
												}
												color={agentForm.values.enterpriseSecondaryColor}
											/>
										</div>
									</Col>
								</Row>
							</div>
						)}
					</Card>

					{isAdmin && isUpdateForm ? (
						<Card
							title={'Admin ONLY'}
							noShadow
							style={{ padding: 20, backgroundColor, marginTop: 20 }}
						>
							<div style={{ marginTop: 10 }}>
								<Row>
									{hasPartnerCommissions ? (
										<Col xs='12' lg='5'>
											<FormLabel
												style={{
													...labelColorStyle,
													fontSize: 14,
													color: COLORS.GRAY,
												}}
											>
												Commission Eligible
											</FormLabel>
											<RadioInput
												name={'commissionEligible'}
												formContainerStyle={{
													display: 'flex',
													flexDirection: 'row',
												}}
												labelStyle={labelColorStyle}
												errors={agentForm.errors.commissionEligible}
												onChange={(e) => {
													agentForm.setFieldValue('commissionEligible', e?.target?.value === 'true')
												}}
												options={[
													{ label: 'Yes', value: true },
													{ label: 'No', value: false },
												]}
											/>
										</Col>
									) : null}
									<Col xs='12' lg='5'>
										<FormLabel
											style={{
												...labelColorStyle,
												fontSize: 14,
												color: COLORS.GRAY,
											}}
										>
											Active
										</FormLabel>
										<RadioInput
											name={'isActive'}
											formContainerStyle={{
												display: 'flex',
												flexDirection: 'row',
											}}
											labelStyle={labelColorStyle}
											errors={agentForm.errors.isActive}
											onChange={(e) => {
												agentForm.setFieldValue('isActive', e?.target?.value === 'true')
											}}
											options={[
												{ label: 'Yes', value: true },
												{ label: 'No', value: false },
											]}
										/>
									</Col>
								</Row>

								<Row>
									<Col xs='12' lg='5'>
										<FormLabel
											style={{
												...labelColorStyle,
												fontSize: 14,
												color: COLORS.GRAY,
											}}
										>
											Show Profile
										</FormLabel>
										<RadioInput
											name={'showProfile'}
											formContainerStyle={{
												display: 'flex',
												flexDirection: 'row',
											}}
											labelStyle={labelColorStyle}
											errors={agentForm.errors.showProfile}
											onChange={(e) => {
												agentForm.setFieldValue('showProfile', e?.target?.value === 'true')
											}}
											options={[
												{ label: 'Yes', value: true },
												{ label: 'No', value: false },
											]}
										/>
									</Col>
								</Row>

								<Row>
									<Col xs='12' lg='5'>
										<FormInput
											type='number'
											id='firstYearDiscountPct'
											name='firstYearDiscountPct'
											label={'FIRST YEAR DISCOUNT %'}
											labelStyle={labelColorStyle}
											errorStyle={errorColorStyle}
											onChange={agentForm.handleChange}
											value={agentForm.values.firstYearDiscountPct}
											placeholder={'0'}
											error={agentForm.errors.firstYearDiscountPct}
										/>
									</Col>
									<Col xs='12' lg='5'>
										<FormInput
											type='number'
											id='renewalDiscountPct'
											name='renewalDiscountPct'
											label={'RENEWAL DISCOUNT %'}
											labelStyle={labelColorStyle}
											errorStyle={errorColorStyle}
											onChange={agentForm.handleChange}
											value={agentForm.values.renewalDiscountPct}
											placeholder={'0'}
											error={agentForm.errors.renewalDiscountPct}
										/>
									</Col>
								</Row>

								<Row>
									<Col xs='12' lg='10'>
										<PartnerCategoriesDropdown
											labelStyle={labelColorStyle}
											value={categoryId}
											style={{ marginBottom: 15 }}
											onSelect={(selectedCategory) => {
												setCategoryId(selectedCategory?.data?.id)
												setCategoryName(selectedCategory?.data?.name)
											}}
										/>
									</Col>
								</Row>
							</div>
						</Card>
					) : null}

					<div>
						{!isUpdateForm && hasPartnerCommissions && (
							<Row>
								<Col>
									<FormInput
										label={
											<Text color={COLORS.GRAY} style={{ margin: 0, padding: 0 }}>
												I agree to {tenantName}'s{' '}
												<Link
													target='_blank'
													href='/partner-agreement'
													style={{ color: primaryColor, display: 'inline' }}
												>
													Partner Agreement
												</Link>
											</Text>
										}
										type='checkbox'
										formContainerStyle={{
											display: 'flex',
											flexDirection: 'row',
											gap: 10,
										}}
										labelStyle={{
											top: 0,
											left: 0,
										}}
										id='tos'
										name='tos'
										style={{ marginBottom: 0 }}
										onChange={agentForm.handleChange}
										value={agentForm.values.tos}
										required
										error={agentForm.touched.tos && agentForm.errors.tos}
									/>
								</Col>
							</Row>
						)}
					</div>
					<Row style={{ marginTop: 20 }}>
						<Col style={{ textAlign: 'left' }}>
							{error && (
								<Card style={{ padding: 10 }}>
									<Text color={'red'} align={'left'} style={{ padding: 0, margin: 0 }}>
										{error}
									</Text>
								</Card>
							)}
							<Button
								style={{
									width: 'auto',
									paddingLeft: 30,
									paddingRight: 30,
									marginTop: 10,
									align: 'left',
								}}
								type='submit'
								id='submit-confirm-publish'
								disabled={loading}
								background={primaryColor}
							>
								{loading ? (
									<>
										<Text style={{ paddingBottom: 0, marginBottom: 0 }} color={COLORS.WHITE}>
											Submitting...{' '}
											<Spinner size={15} style={{ paddingLeft: 10 }} color={COLORS.WHITE} />
										</Text>{' '}
									</>
								) : isUpdateForm ? (
									'Update Profile'
								) : (
									'Create My Profile!'
								)}
							</Button>
						</Col>
					</Row>
				</form>
			</FormikProvider>
		</>
	)
}
